/**
 * Purpose: Visual identity header for every business — contained cover, small logo,
 *          name, badges, rating, description, meta line, tags.
 * Inputs: business, views, onCoverClick (lightbox opener or undefined).
 * Outputs: hero block; renders only fields that exist (future fields like
 *          business.tags / business.logo light up automatically when the
 *          backend ships them — no hero changes needed).
 *
 * Directory hero, not a social profile: the cover aligns with the page container
 * and stays short so the name, description and actions sit above the fold.
 */
import React from 'react';
import ListingCoverHero from '../../common/ListingCoverHero';
import LazyImage from '../../common/LazyImage';
import { Badge, RatingStars, ExpandableText } from '../../ui';
import { getListingCoverImageUrl } from '../../../utils/imageFallback';
import { formatDate } from '../../../utils/helpers';

const CONTAINER = 'mx-auto max-w-screen-2xl px-4 sm:px-6 lg:px-8';

const COVER_IMAGE_CLASS = 'w-full h-36 sm:h-44 md:h-52 lg:h-64 object-cover object-center';

const formatCategory = (category) =>
  (category || 'other').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const EyeIcon = (
  <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
    />
  </svg>
);

const Separator = () => (
  <span className="text-gray-300 dark:text-gray-600" aria-hidden>
    •
  </span>
);

const BusinessHero = ({ business, views = 0, onCoverClick }) => {
  const logoUrl =
    business.logo?.thumbnail || business.logo?.medium_image || business.logo?.original_image || null;
  const rating = Number(business.average_rating || 0);
  const reviewCount = business.total_reviews || 0;
  const tags = Array.isArray(business.tags) ? business.tags : [];

  return (
    <header className="animate-[fadeIn_0.3s_ease-out]">
      <div className={`${CONTAINER} pt-4`}>
        <ListingCoverHero
          src={getListingCoverImageUrl(business)}
          alt={business.name}
          onClick={onCoverClick}
          className="rounded-2xl ring-1 ring-gray-900/5 dark:ring-white/10"
          imageClassName={COVER_IMAGE_CLASS}
        />
      </div>

      <div className={`${CONTAINER} pb-4 pt-4`}>
        <div className="flex items-start gap-3 sm:gap-4">
          {/* Logo tile (initials fallback until Business.logo exists) */}
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-brand-600 text-lg font-bold text-white dark:border-gray-700 sm:h-14 sm:w-14 sm:text-xl">
            {logoUrl ? (
              <LazyImage src={logoUrl} alt={`${business.name} logo`} className="h-full w-full" showProgress={false} />
            ) : (
              <span aria-hidden>{(business.name || '?').charAt(0).toUpperCase()}</span>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
              <h1 className="text-xl font-bold leading-tight text-gray-900 dark:text-gray-100 sm:text-2xl lg:text-3xl">
                {business.name}
              </h1>
              {business.is_approved && <Badge variant="verified" size="sm">Verified</Badge>}
              {(business.is_sponsored || business.is_featured) && (
                <Badge variant="featured" size="sm">{business.is_sponsored ? 'Sponsored' : 'Featured'}</Badge>
              )}
            </div>

            <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-sm text-gray-700 dark:text-gray-300">
              <span className="font-medium">{formatCategory(business.category)}</span>
              {(rating > 0 || reviewCount > 0) && (
                <>
                  <Separator />
                  <RatingStars value={rating} showValue count={reviewCount} size="sm" />
                </>
              )}
            </div>

            {business.description && (
              <div className="mt-2 max-w-3xl">
                <ExpandableText
                  lines={2}
                  className="text-sm leading-relaxed text-gray-600 dark:text-gray-300"
                >
                  {business.description}
                </ExpandableText>
              </div>
            )}

            <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
              <span className="inline-flex items-center gap-1" title="Total views">
                {EyeIcon}
                {views} views
              </span>
              {business.society?.name && (
                <>
                  <Separator />
                  <span title="Society">{business.society.name}</span>
                </>
              )}
              {business.created_at && (
                <>
                  <Separator />
                  <span title="Listed since">Since {formatDate(business.created_at)}</span>
                </>
              )}
            </div>

            {tags.length > 0 && (
              <div className="mt-2.5 flex flex-wrap gap-1.5">
                {tags.map((tag) => (
                  <Badge key={tag} variant="neutral" size="sm">{tag}</Badge>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default BusinessHero;
